import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, updateDoc, deleteDoc, doc, writeBatch, addDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase/config';
import * as XLSX from 'xlsx';
import { 
    Package, Edit2, Trash2, Eye, EyeOff, Search, TrendingUp, 
    ChevronLeft, ChevronRight, Download, 
    FileSpreadsheet, Images, Copy, Plus, CheckCircle, XCircle,
    RefreshCw
} from 'lucide-react';

const ProductManagement = ({ userInfo }) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [priceRange] = useState({ min: '', max: '' });
    const [sortBy, setSortBy] = useState('createdAt-desc');
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    // 페이지네이션
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    // 엑셀/이미지 업로드 관련 상태
    const [showExcelModal, setShowExcelModal] = useState(false);
    const [showImageUploadModal, setShowImageUploadModal] = useState(false);
    const [excelData, setExcelData] = useState([]);
    const [excelPreview, setExcelPreview] = useState([]);
    const [uploadingBulk, setUploadingBulk] = useState(false);
    const [uploadedImages, setUploadedImages] = useState([]);
    const [uploadingImages, setUploadingImages] = useState(false);

    const categories = [
        { value: 'all', label: '전체' },
        { value: 'implant', label: '임플란트' },
        { value: 'restorative', label: '수복재료' },
        { value: 'endodontic', label: '근관치료' },
        { value: 'preventive', label: '예방치료' },
        { value: 'impression', label: '인상재료' },
        { value: 'orthodontic', label: '교정재료' },
        { value: 'surgical', label: '외과용품' },
        { value: 'hygiene', label: '위생용품' },
        { value: 'equipment', label: '장비' },
        { value: 'lab-material', label: '기공재료' },
        { value: 'other', label: '기타' }
    ];

    const sortOptions = [
        { value: 'createdAt-desc', label: '최신순' },
        { value: 'createdAt-asc', label: '오래된순' },
        { value: 'price-asc', label: '낮은 가격순' },
        { value: 'price-desc', label: '높은 가격순' },
        { value: 'viewCount-desc', label: '조회수 높은순' },
        { value: 'orderCount-desc', label: '판매량 높은순' },
        { value: 'name-asc', label: '이름순(ㄱ-ㅎ)' }
    ];

    const loadProducts = useCallback(async () => {
        try {
            setLoading(true);
            const q = query(
                collection(db, 'marketplaceProducts'),
                where('sellerId', '==', userInfo.uid)
            );

            const snapshot = await getDocs(q);
            const productsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            console.log(`✅ ${productsData.length}개 상품 로드 완료`);
            setProducts(productsData);
        } catch (error) {
            console.error('❌ 상품 로딩 오류:', error);
            alert('상품을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [userInfo]);

    useEffect(() => {
        if (!userInfo) {
            alert('로그인이 필요합니다.');
            window.location.href = '/login';
            return;
        }

        if (userInfo.sellerStatus !== 'approved') {
            alert('판매자 승인 후 이용 가능합니다.');
            window.location.href = '/seller-application';
            return;
        }

        loadProducts();
    }, [userInfo, loadProducts]);

    // 엑셀 템플릿 다운로드
    const downloadExcelTemplate = () => {
        // 예시 데이터 2개
        const templateData = [
            {
                '상품명': '오스템 TS III SA 임플란트 픽스처',
                '브랜드': 'Osstem',
                '카테고리': 'implant',
                '세부카테고리': 'implant-fixture',
                '상품설명': '탁월한 초기 고정력과 골유착률을 자랑하는 프리미엄 임플란트 픽스처',
                '판매가': 85000,
                '정가': 100000,
                '재고': 100,
                '단위': 'EA',
                '최소주문수량': 1,
                '제조사': '오스템임플란트',
                '원산지': '대한민국',
                '제품사양': '직경: 4.0mm, 길이: 10mm, 재질: 순수 티타늄 Grade 4',
                '주요특징': 'SA 표면처리, 우수한 초기 안정성, 빠른 골유착',
                '인증정보': 'FDA, CE, KFDA, ISO 13485',
                '배송정보': '무료배송',
                '반품정책': '7일 이내 반품 가능',
                '옵션1명': '직경',
                '옵션1값': '3.5mm,4.0mm,4.5mm,5.0mm',
                '옵션1추가금액': '0,0,5000,10000',
                '옵션2명': '길이',
                '옵션2값': '8.5mm,10mm,11.5mm,13mm',
                '옵션2추가금액': '0,0,0,0',
                '이미지URL1': '',
                '이미지URL2': '',
                '이미지URL3': ''
            },
            {
                '상품명': '3M ESPE Filtek Z350 XT 컴포짓 레진',
                '브랜드': '3M',
                '카테고리': 'restorative',
                '세부카테고리': 'restorative-composite',
                '상품설명': '우수한 심미성과 내구성을 갖춘 나노필러 유니버설 컴포짓 레진',
                '판매가': 45000,
                '정가': 55000,
                '재고': 200,
                '단위': 'SYRINGE',
                '최소주문수량': 1,
                '제조사': '3M',
                '원산지': '미국',
                '제품사양': '용량: 4g, 타입: 시린지',
                '주요특징': '나노필러 기술, 뛰어난 연마성, 자연스러운 형광',
                '인증정보': 'FDA, CE',
                '배송정보': '무료배송',
                '반품정책': '개봉 전 반품 가능',
                '옵션1명': '쉐이드',
                '옵션1값': 'A1,A2,A3,A3.5,B1,B2',
                '옵션1추가금액': '0,0,0,0,0,0',
                '옵션2명': '',
                '옵션2값': '',
                '옵션2추가금액': '',
                '이미지URL1': '',
                '이미지URL2': '',
                '이미지URL3': ''
            }
        ];

        const ws = XLSX.utils.json_to_sheet(templateData);
        ws['!cols'] = [
            { wch: 40 }, // 상품명
            { wch: 15 }, // 브랜드
            { wch: 15 }, // 카테고리
            { wch: 20 }, // 세부카테고리
            { wch: 50 }, // 상품설명
            { wch: 12 }, // 판매가
            { wch: 12 }, // 정가
            { wch: 10 }, // 재고
            { wch: 10 }, // 단위
            { wch: 12 }, // 최소주문수량
            { wch: 18 }, // 제조사
            { wch: 12 }, // 원산지
            { wch: 50 }, // 제품사양
            { wch: 50 }, // 주요특징
            { wch: 25 }, // 인증정보
            { wch: 15 }, // 배송정보
            { wch: 20 }, // 반품정책
            { wch: 15 }, // 옵션1명
            { wch: 40 }, // 옵션1값
            { wch: 30 }, // 옵션1추가금액
            { wch: 15 }, // 옵션2명
            { wch: 40 }, // 옵션2값
            { wch: 30 }, // 옵션2추가금액
            { wch: 60 }, // 이미지URL1
            { wch: 60 }, // 이미지URL2
            { wch: 60 }  // 이미지URL3
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '상품목록');

        // 카테고리 목록 시트 (새로운 전문 카테고리)
        const categorySheet = XLSX.utils.json_to_sheet([
            { '메인카테고리': 'implant', '설명': '임플란트', '세부카테고리': 'implant-fixture, implant-abutment, implant-kit, implant-bone, implant-membrane, implant-accessory' },
            { '메인카테고리': 'restorative', '설명': '수복재료', '세부카테고리': 'restorative-composite, restorative-cement, restorative-adhesive, restorative-temporary' },
            { '메인카테고리': 'endodontic', '설명': '근관치료', '세부카테고리': 'endo-file, endo-sealer, endo-guttapercha, endo-irrigant' },
            { '메인카테고리': 'preventive', '설명': '예방치료', '세부카테고리': 'preventive-fluoride, preventive-sealant, preventive-polish' },
            { '메인카테고리': 'impression', '설명': '인상재료', '세부카테고리': 'impression-silicone, impression-alginate, impression-tray' },
            { '메인카테고리': 'orthodontic', '설명': '교정재료', '세부카테고리': 'ortho-bracket, ortho-wire, ortho-elastic, ortho-accessory' },
            { '메인카테고리': 'surgical', '설명': '외과용품', '세부카테고리': 'surgical-suture, surgical-blade, surgical-instrument' },
            { '메인카테고리': 'hygiene', '설명': '위생용품', '세부카테고리': 'hygiene-glove, hygiene-mask, hygiene-sterilization, hygiene-disposable' },
            { '메인카테고리': 'equipment', '설명': '장비', '세부카테고리': 'equip-handpiece, equip-light, equip-scaler, equip-xray, equip-other' },
            { '메인카테고리': 'lab-material', '설명': '기공재료', '세부카테고리': 'lab-zirconia, lab-porcelain, lab-wax, lab-alloy' },
            { '메인카테고리': 'other', '설명': '기타', '세부카테고리': '' }
        ]);
        XLSX.utils.book_append_sheet(wb, categorySheet, '카테고리 목록');

        // 단위 목록 시트
        const unitSheet = XLSX.utils.json_to_sheet([
            { '단위코드': 'EA', '설명': '개' },
            { '단위코드': 'BOX', '설명': '박스' },
            { '단위코드': 'SET', '설명': '세트' },
            { '단위코드': 'PACK', '설명': '팩' },
            { '단위코드': 'KIT', '설명': '키트' },
            { '단위코드': 'BOTTLE', '설명': '병' },
            { '단위코드': 'TUBE', '설명': '튜브' },
            { '단위코드': 'SYRINGE', '설명': '시린지' },
            { '단위코드': 'KG', '설명': 'kg' },
            { '단위코드': 'G', '설명': 'g' },
            { '단위코드': 'ML', '설명': 'ml' },
            { '단위코드': 'L', '설명': 'L' }
        ]);
        XLSX.utils.book_append_sheet(wb, unitSheet, '단위 목록');

        // 사용안내 시트
        const guideSheet = XLSX.utils.json_to_sheet([
            { '단계': '1', '설명': '상품 정보를 입력하세요 (필수: 상품명, 카테고리, 판매가, 재고)' },
            { '단계': '2', '설명': '카테고리는 "카테고리 목록" 시트의 메인카테고리 값을 입력하세요' },
            { '단계': '3', '설명': '세부카테고리는 해당 메인카테고리의 세부카테고리 값을 입력하세요 (선택)' },
            { '단계': '4', '설명': '단위는 "단위 목록" 시트의 단위코드를 입력하세요 (예: EA, BOX, SET)' },
            { '단계': '5', '설명': '옵션이 있는 경우: 옵션명(예: 직경), 옵션값(콤마로 구분), 추가금액(콤마로 구분)을 입력' },
            { '단계': '6', '설명': '옵션값과 추가금액의 개수는 동일해야 합니다 (예: 값 4개면 금액도 4개)' },
            { '단계': '7', '설명': '이미지 일괄 업로드 기능으로 이미지를 먼저 업로드하고 URL을 복사하세요' },
            { '단계': '8', '설명': '완성된 엑셀 파일을 "엑셀 일괄등록" 버튼으로 업로드하세요' }
        ]);
        XLSX.utils.book_append_sheet(wb, guideSheet, '사용안내');

        XLSX.writeFile(wb, '치과재료_상품등록_템플릿_v3.xlsx');
        alert('✅ 엑셀 템플릿이 다운로드되었습니다!\n\n• 새로운 치과 전문 카테고리\n• 옵션/추가상품 지원\n• 상세 사용안내 포함');
    };

    // 여러 이미지 일괄 업로드
    const handleMultipleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        try {
            setUploadingImages(true);
            const uploadPromises = files.map(async (file, index) => {
                const storageRef = ref(storage, `marketplace/${Date.now()}_${index}_${file.name}`);
                await uploadBytes(storageRef, file);
                const url = await getDownloadURL(storageRef);
                return {
                    fileName: file.name,
                    url: url
                };
            });

            const results = await Promise.all(uploadPromises);
            setUploadedImages(results);
            setShowImageUploadModal(true);

            alert(`✅ ${results.length}개의 이미지가 업로드되었습니다!`);
        } catch (error) {
            console.error('❌ 이미지 업로드 실패:', error);
            alert('이미지 업로드에 실패했습니다: ' + error.message);
        } finally {
            setUploadingImages(false);
        }
    };

    // 이미지 URL 엑셀로 다운로드
    const downloadImageUrls = () => {
        if (uploadedImages.length === 0) {
            alert('업로드된 이미지가 없습니다.');
            return;
        }

        const data = uploadedImages.map((img, index) => ({
            '번호': index + 1,
            '파일명': img.fileName,
            '이미지URL': img.url
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        ws['!cols'] = [{ wch: 8 }, { wch: 30 }, { wch: 80 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '이미지 URL 목록');
        XLSX.writeFile(wb, '이미지_URL_목록.xlsx');

        alert('이미지 URL 목록이 다운로드되었습니다!');
    };

    // URL 복사
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            alert('URL이 복사되었습니다!');
        }).catch(err => {
            console.error('복사 실패:', err);
            alert('복사에 실패했습니다.');
        });
    };

    // 옵션 파싱 헬퍼 함수
    const parseOptions = (row) => {
        const options = [];

        // 옵션1 파싱
        if (row['옵션1명'] && row['옵션1값']) {
            const values = row['옵션1값'].split(',').map(v => v.trim()).filter(v => v);
            const prices = row['옵션1추가금액']
                ? row['옵션1추가금액'].split(',').map(p => parseInt(p.trim()) || 0)
                : values.map(() => 0);

            if (values.length > 0) {
                options.push({
                    name: row['옵션1명'].trim(),
                    values: values.map((v, i) => ({
                        value: v,
                        additionalPrice: prices[i] || 0
                    }))
                });
            }
        }

        // 옵션2 파싱
        if (row['옵션2명'] && row['옵션2값']) {
            const values = row['옵션2값'].split(',').map(v => v.trim()).filter(v => v);
            const prices = row['옵션2추가금액']
                ? row['옵션2추가금액'].split(',').map(p => parseInt(p.trim()) || 0)
                : values.map(() => 0);

            if (values.length > 0) {
                options.push({
                    name: row['옵션2명'].trim(),
                    values: values.map((v, i) => ({
                        value: v,
                        additionalPrice: prices[i] || 0
                    }))
                });
            }
        }

        return options;
    };

    // 엑셀 파일 업로드 및 파싱
    const handleExcelUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            const validatedData = jsonData.map((row, index) => {
                const errors = [];
                if (!row['상품명']) errors.push('상품명 필수');
                if (!row['카테고리']) errors.push('카테고리 필수');
                if (!row['판매가']) errors.push('판매가 필수');

                const validCategories = categories.filter(c => c.value !== 'all').map(c => c.value);
                if (row['카테고리'] && !validCategories.includes(row['카테고리'])) {
                    errors.push('잘못된 카테고리');
                }

                // 옵션 유효성 검사
                if (row['옵션1명'] && !row['옵션1값']) {
                    errors.push('옵션1값 필수');
                }
                if (row['옵션2명'] && !row['옵션2값']) {
                    errors.push('옵션2값 필수');
                }

                // 이미지 URL 수집 (최대 5개)
                const imageUrls = [];
                for (let i = 1; i <= 5; i++) {
                    const urlKey = `이미지URL${i}`;
                    if (row[urlKey] && row[urlKey].toString().trim()) {
                        imageUrls.push(row[urlKey].toString().trim());
                    }
                }

                // 옵션 파싱
                const options = parseOptions(row);

                return {
                    index: index + 1,
                    name: row['상품명'] || '',
                    brand: row['브랜드'] || '',
                    category: row['카테고리'] || 'other',
                    subCategory: row['세부카테고리'] || '',
                    description: row['상품설명'] || '',
                    price: parseInt(row['판매가']) || 0,
                    originalPrice: row['정가'] ? parseInt(row['정가']) : null,
                    discount: row['할인율'] ? parseInt(row['할인율']) : null,
                    stock: row['재고'] ? parseInt(row['재고']) : 0,
                    unit: row['단위'] || 'EA',
                    minOrder: row['최소주문수량'] ? parseInt(row['최소주문수량']) : 1,
                    specifications: row['제품사양'] || '',
                    features: row['주요특징'] || '',
                    imageUrls: imageUrls,
                    manufacturer: row['제조사'] || '',
                    origin: row['원산지'] || '',
                    certifications: row['인증정보'] || '',
                    shippingInfo: row['배송정보'] || '',
                    returnPolicy: row['반품정책'] || '',
                    options: options,
                    hasOptions: options.length > 0,
                    errors: errors,
                    isValid: errors.length === 0
                };
            });

            setExcelData(validatedData);
            setExcelPreview(validatedData);
            setShowExcelModal(true);

            const invalidCount = validatedData.filter(d => !d.isValid).length;
            const optionCount = validatedData.filter(d => d.hasOptions).length;

            if (invalidCount > 0) {
                alert(`${invalidCount}개의 상품에 오류가 있습니다. 미리보기를 확인해주세요.`);
            } else {
                alert(`✅ ${validatedData.length}개의 상품을 확인했습니다.\n옵션 포함 상품: ${optionCount}개`);
            }
        } catch (error) {
            console.error('엑셀 파일 처리 실패:', error);
            alert('엑셀 파일을 읽는데 실패했습니다. 파일 형식을 확인해주세요.');
        }
    };

    // 엑셀 데이터 일괄 업로드
    const handleBulkUpload = async () => {
        const validProducts = excelData.filter(p => p.isValid);

        if (validProducts.length === 0) {
            alert('업로드할 수 있는 유효한 상품이 없습니다.');
            return;
        }

        const optionCount = validProducts.filter(p => p.hasOptions).length;
        if (!window.confirm(`${validProducts.length}개의 상품을 등록하시겠습니까?\n(옵션 포함 상품: ${optionCount}개)`)) {
            return;
        }

        try {
            setUploadingBulk(true);

            for (const product of validProducts) {
                const productData = {
                    // 기본 정보
                    name: product.name,
                    brand: product.brand,
                    category: product.category,
                    subCategory: product.subCategory,
                    description: product.description,

                    // 가격 정보
                    price: product.price,
                    originalPrice: product.originalPrice,
                    discount: product.discount,

                    // 재고 및 주문
                    stock: product.stock,
                    unit: product.unit,
                    minOrder: product.minOrder || 1,

                    // 상세 정보
                    specifications: product.specifications,
                    features: product.features,
                    manufacturer: product.manufacturer,
                    origin: product.origin,
                    certifications: product.certifications,

                    // 배송/반품
                    shippingInfo: product.shippingInfo,
                    returnPolicy: product.returnPolicy,

                    // 이미지
                    images: product.imageUrls || [],
                    imageUrls: product.imageUrls || [],

                    // 옵션 (네이버 스타일)
                    options: product.options || [],
                    hasOptions: product.hasOptions || false,

                    // 판매자 정보
                    sellerId: userInfo.uid,
                    sellerName: userInfo.businessName || userInfo.name || userInfo.email,

                    // 상태 및 통계
                    status: 'active',
                    createdAt: Timestamp.now(),
                    updatedAt: Timestamp.now(),
                    viewCount: 0,
                    orderCount: 0
                };

                await addDoc(collection(db, 'marketplaceProducts'), productData);
            }

            alert(`✅ ${validProducts.length}개의 상품이 성공적으로 등록되었습니다!\n옵션 포함 상품: ${optionCount}개`);
            setShowExcelModal(false);
            setExcelData([]);
            setExcelPreview([]);
            loadProducts();
        } catch (error) {
            console.error('일괄 업로드 실패:', error);
            alert('상품 등록 중 오류가 발생했습니다: ' + error.message);
        } finally {
            setUploadingBulk(false);
        }
    };

    const toggleProductStatus = async (productId, currentStatus) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';

        try {
            await updateDoc(doc(db, 'marketplaceProducts', productId), {
                status: newStatus,
                updatedAt: new Date()
            });

            setProducts(prev => prev.map(p =>
                p.id === productId ? { ...p, status: newStatus } : p
            ));

            alert(newStatus === 'active' ? '상품이 활성화되었습니다.' : '상품이 비활성화되었습니다.');
        } catch (error) {
            console.error('상태 변경 오류:', error);
            alert('상태 변경에 실패했습니다.');
        }
    };

    const deleteProduct = async (productId) => {
        if (!window.confirm('정말 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
            return;
        }

        try {
            await deleteDoc(doc(db, 'marketplaceProducts', productId));
            setProducts(prev => prev.filter(p => p.id !== productId));
            alert('상품이 삭제되었습니다.');
        } catch (error) {
            console.error('삭제 오류:', error);
            alert('삭제에 실패했습니다.');
        }
    };

    const handleSelectProduct = (productId) => {
        setSelectedProducts(prev =>
            prev.includes(productId)
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(currentProducts.map(p => p.id));
        }
        setSelectAll(!selectAll);
    };

    const bulkDelete = async () => {
        if (selectedProducts.length === 0) {
            alert('삭제할 상품을 선택해주세요.');
            return;
        }

        if (!window.confirm(`선택한 ${selectedProducts.length}개의 상품을 삭제하시겠습니까?`)) {
            return;
        }

        try {
            const batch = writeBatch(db);
            selectedProducts.forEach(productId => {
                batch.delete(doc(db, 'marketplaceProducts', productId));
            });
            await batch.commit();

            setProducts(prev => prev.filter(p => !selectedProducts.includes(p.id)));
            setSelectedProducts([]);
            setSelectAll(false);
            alert('선택한 상품이 삭제되었습니다.');
        } catch (error) {
            console.error('일괄 삭제 오류:', error);
            alert('일괄 삭제에 실패했습니다.');
        }
    };

    const bulkUpdateStatus = async (newStatus) => {
        if (selectedProducts.length === 0) {
            alert('상태를 변경할 상품을 선택해주세요.');
            return;
        }

        try {
            const batch = writeBatch(db);
            selectedProducts.forEach(productId => {
                batch.update(doc(db, 'marketplaceProducts', productId), {
                    status: newStatus,
                    updatedAt: new Date()
                });
            });
            await batch.commit();

            setProducts(prev => prev.map(p =>
                selectedProducts.includes(p.id) ? { ...p, status: newStatus } : p
            ));
            setSelectedProducts([]);
            setSelectAll(false);
            alert('선택한 상품의 상태가 변경되었습니다.');
        } catch (error) {
            console.error('일괄 상태 변경 오류:', error);
            alert('일괄 상태 변경에 실패했습니다.');
        }
    };

    const filteredProducts = products.filter(product => {
        const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = filterCategory === 'all' || product.category === filterCategory;
        const matchesStatus = filterStatus === 'all' || (filterStatus === 'soldout' ? (product.stock === 0 || product.soldOut) : product.status === filterStatus);
        const matchesPriceMin = !priceRange.min || product.price >= parseInt(priceRange.min);
        const matchesPriceMax = !priceRange.max || product.price <= parseInt(priceRange.max);

        return matchesSearch && matchesCategory && matchesStatus && matchesPriceMin && matchesPriceMax;
    }).sort((a, b) => {
        const [field, order] = sortBy.split('-');
        let comparison = 0;

        if (field === 'name') {
            comparison = a.name.localeCompare(b.name, 'ko');
        } else if (field === 'price' || field === 'viewCount' || field === 'orderCount') {
            comparison = (a[field] || 0) - (b[field] || 0);
        } else if (field === 'createdAt') {
            const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt);
            const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt);
            comparison = aTime - bTime;
        }

        return order === 'desc' ? -comparison : comparison;
    });

    const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentProducts = filteredProducts.slice(startIndex, endIndex);

    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getCategoryLabel = (value) => {
        const category = categories.find(c => c.value === value);
        return category ? category.label : value;
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('ko-KR').format(price);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return '-';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('ko-KR', { year: '2-digit', month: '2-digit', day: '2-digit' });
    };

    const getStatusBadge = (status) => {
        const badgeStyles = {
            active: { bg: '#dcfce7', color: '#166534', text: '판매중' },
            inactive: { bg: '#f3f4f6', color: '#4b5563', text: '판매중지' },
            soldout: { bg: '#fee2e2', color: '#991b1b', text: '품절' },
            pending: { bg: '#fef3c7', color: '#92400e', text: '검수대기' },
            rejected: { bg: '#fee2e2', color: '#991b1b', text: '반려' }
        };
        const style = badgeStyles[status] || badgeStyles.inactive;

        return (
            <span style={{
                padding: '4px 8px',
                fontSize: '11px',
                fontWeight: '700',
                backgroundColor: style.bg,
                color: style.color,
                borderRadius: '4px',
                whiteSpace: 'nowrap'
            }}>
                {style.text}
            </span>
        );
    };

    // 🔥 이미지 URL 가져오기 함수 - images 배열과 imageUrl 모두 지원
    const getProductImageUrl = (product) => {
        // 1. images 배열이 있고 첫 번째 요소가 있으면 사용
        if (product.images && Array.isArray(product.images) && product.images.length > 0) {
            return product.images[0];
        }
        // 2. imageUrl 문자열이 있으면 사용
        if (product.imageUrl && typeof product.imageUrl === 'string') {
            return product.imageUrl;
        }
        // 3. 둘 다 없으면 null
        return null;
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <div style={styles.loadingText}>상품 목록을 불러오는 중...</div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            {/* 헤더 */}
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <Package size={32} color="#6366f1" />
                    <div>
                        <h1 style={styles.title}>상품 관리</h1>
                        <p style={styles.subtitle}>
                            전체 {products.length}개 상품
                            {selectedProducts.length > 0 && ` · ${selectedProducts.length}개 선택됨`}
                        </p>
                    </div>
                </div>
                <div style={styles.headerButtons}>
                    <button onClick={downloadExcelTemplate} style={styles.templateButton}>
                        <Download size={18} />
                        엑셀 양식
                    </button>

                    <label style={styles.imageUploadButton}>
                        <Images size={18} />
                        {uploadingImages ? '업로드 중...' : '이미지 업로드'}
                        <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleMultipleImageUpload}
                            style={{ display: 'none' }}
                            disabled={uploadingImages}
                        />
                    </label>

                    <label style={styles.excelUploadButton}>
                        <FileSpreadsheet size={18} />
                        엑셀 일괄등록
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            onChange={handleExcelUpload}
                            style={{ display: 'none' }}
                        />
                    </label>

                    <button
                        style={styles.addButton}
                        onClick={() => window.location.href = '/product-registration'}
                    >
                        <Plus size={18} />
                        상품 등록
                    </button>

                    <button
                        style={styles.refreshButton}
                        onClick={loadProducts}
                    >
                        <RefreshCw size={18} />
                    </button>
                </div>
            </div>

            {/* 일괄 작업 바 */}
            {selectedProducts.length > 0 && (
                <div style={styles.bulkActionsBar}>
                    <span style={styles.bulkActionsText}>{selectedProducts.length}개 선택됨</span>
                    <div style={styles.bulkActions}>
                        <button
                            style={styles.bulkActionButton}
                            onClick={() => bulkUpdateStatus('active')}
                        >
                            <Eye size={16} />
                            판매시작
                        </button>
                        <button
                            style={styles.bulkActionButton}
                            onClick={() => bulkUpdateStatus('inactive')}
                        >
                            <EyeOff size={16} />
                            판매중지
                        </button>
                        <button
                            style={{ ...styles.bulkActionButton, ...styles.bulkDeleteButton }}
                            onClick={bulkDelete}
                        >
                            <Trash2 size={16} />
                            삭제
                        </button>
                        <button
                            style={styles.bulkCancelButton}
                            onClick={() => {
                                setSelectedProducts([]);
                                setSelectAll(false);
                            }}
                        >
                            취소
                        </button>
                    </div>
                </div>
            )}

            {/* 필터 영역 */}
            <div style={styles.filterSection}>
                <div style={styles.filterRow}>
                    <div style={styles.searchBox}>
                        <Search size={18} color="#64748b" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="상품명 검색..."
                            style={styles.searchInput}
                        />
                    </div>

                    <select
                        value={filterCategory}
                        onChange={(e) => setFilterCategory(e.target.value)}
                        style={styles.select}
                    >
                        {categories.map(cat => (
                            <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                    </select>

                    <div style={{display:'flex',gap:'4px',backgroundColor:'#f1f5f9',borderRadius:'10px',padding:'3px'}}>
                        {[
                            { value: 'all', label: '전체' },
                            { value: 'active', label: '활성' },
                            { value: 'pending', label: '대기(검수중)' },
                            { value: 'rejected', label: '반려' },
                            { value: 'soldout', label: '품절' }
                        ].map(tab => (
                            <button
                                key={tab.value}
                                onClick={() => { setFilterStatus(tab.value); setCurrentPage(1); }}
                                style={{
                                    padding:'6px 14px',
                                    borderRadius:'8px',
                                    border:'none',
                                    fontSize:'13px',
                                    fontWeight: filterStatus === tab.value ? '700' : '500',
                                    backgroundColor: filterStatus === tab.value ? '#ffffff' : 'transparent',
                                    color: filterStatus === tab.value ? '#6366f1' : '#64748b',
                                    boxShadow: filterStatus === tab.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                    cursor:'pointer',
                                    transition:'all 0.2s',
                                    whiteSpace:'nowrap'
                                }}
                            >
                                {tab.label} ({products.filter(p => tab.value === 'all' ? true : tab.value === 'soldout' ? (p.stock === 0 || p.soldOut) : p.status === tab.value).length})
                            </button>
                        ))}
                    </div>

                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        style={styles.select}
                    >
                        {sortOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>

                    <select
                        value={itemsPerPage}
                        onChange={(e) => {
                            setItemsPerPage(parseInt(e.target.value));
                            setCurrentPage(1);
                        }}
                        style={styles.select}
                    >
                        <option value="20">20개씩</option>
                        <option value="50">50개씩</option>
                        <option value="100">100개씩</option>
                    </select>
                </div>
            </div>

            {/* 테이블 */}
            {filteredProducts.length === 0 ? (
                <div style={styles.emptyState}>
                    <Package size={64} color="#cbd5e1" />
                    <p style={styles.emptyText}>
                        {searchTerm || filterCategory !== 'all' || filterStatus !== 'all'
                            ? '검색 결과가 없습니다.'
                            : '등록된 상품이 없습니다.'}
                    </p>
                    {!searchTerm && filterCategory === 'all' && filterStatus === 'all' && (
                        <button
                            style={styles.emptyButton}
                            onClick={() => window.location.href = '/product-registration'}
                        >
                            첫 상품 등록하기
                        </button>
                    )}
                </div>
            ) : (
                <>
                    <div style={styles.tableContainer}>
                        <table style={styles.table}>
                            <thead>
                                <tr style={styles.tableHeader}>
                                    <th style={{ ...styles.th, width: '40px' }}>
                                        <input
                                            type="checkbox"
                                            checked={selectAll && currentProducts.length > 0}
                                            onChange={handleSelectAll}
                                            style={styles.checkbox}
                                        />
                                    </th>
                                    <th style={{ ...styles.th, width: '60px' }}>이미지</th>
                                    <th style={{ ...styles.th, width: 'auto' }}>상품명</th>
                                    <th style={{ ...styles.th, width: '100px' }}>카테고리</th>
                                    <th style={{ ...styles.th, width: '100px' }}>판매가</th>
                                    <th style={{ ...styles.th, width: '70px' }}>재고</th>
                                    <th style={{ ...styles.th, width: '80px' }}>상태</th>
                                    <th style={{ ...styles.th, width: '80px' }}>조회/판매</th>
                                    <th style={{ ...styles.th, width: '90px' }}>등록일</th>
                                    <th style={{ ...styles.th, width: '200px' }}>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentProducts.map(product => {
                                    const imageUrl = getProductImageUrl(product);

                                    return (
                                        <tr key={product.id} style={styles.tableRow}>
                                            <td style={styles.td}>
                                                <input
                                                    type="checkbox"
                                                    checked={selectedProducts.includes(product.id)}
                                                    onChange={() => handleSelectProduct(product.id)}
                                                    style={styles.checkbox}
                                                />
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.thumbnail}>
                                                    {imageUrl ? (
                                                        <img
                                                            src={imageUrl}
                                                            alt={product.name}
                                                            style={styles.thumbnailImg}
                                                            onError={(e) => {
                                                                console.error('이미지 로딩 실패:', imageUrl);
                                                                e.target.onerror = null;
                                                                e.target.style.display = 'none';
                                                                const parent = e.target.parentElement;
                                                                if (parent) {
                                                                    parent.innerHTML = `
                                                                        <div style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 4px;">
                                                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" stroke-width="2">
                                                                                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                                                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                                                                <polyline points="21 15 16 10 5 21"></polyline>
                                                                            </svg>
                                                                            <span style="font-size: 9px; color: #94a3b8;">로딩실패</span>
                                                                        </div>
                                                                    `;
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        <div style={styles.noThumbnail}>
                                                            <Package size={20} color="#cbd5e1" />
                                                            <span style={{ fontSize: '9px', color: '#94a3b8', marginTop: '2px' }}>이미지없음</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.productNameCell}>
                                                    <div style={styles.productNameText}>{product.name}</div>
                                                    {product.brand && (
                                                        <div style={styles.productBrand}>{product.brand}</div>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={styles.categoryTag}>
                                                    {getCategoryLabel(product.category)}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.priceCell}>
                                                    <span style={styles.price}>{formatPrice(product.price)}원</span>
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={product.stock > 0 ? styles.stockAvailable : styles.stockOut}>
                                                    {product.stock || 0}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                {getStatusBadge(product.status)}
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.statsCell}>
                                                    <div style={styles.statItem}>
                                                        <Eye size={12} />
                                                        {product.viewCount || 0}
                                                    </div>
                                                    <div style={styles.statItem}>
                                                        <TrendingUp size={12} />
                                                        {product.orderCount || 0}
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={styles.td}>
                                                <span style={styles.dateText}>
                                                    {formatDate(product.createdAt)}
                                                </span>
                                            </td>
                                            <td style={styles.td}>
                                                <div style={styles.actionButtons}>
                                                    <button
                                                        style={styles.actionBtn}
                                                        onClick={() => window.location.href = `/product-edit/${product.id}`}
                                                        title="수정"
                                                    >
                                                        <Edit2 size={14} />
                                                    </button>

                                                    <button
                                                        style={{
                                                            ...styles.actionBtn,
                                                            ...(product.status === 'active' ? styles.actionBtnWarning : styles.actionBtnSuccess)
                                                        }}
                                                        onClick={() => toggleProductStatus(product.id, product.status)}
                                                        title={product.status === 'active' ? '판매중지' : '판매시작'}
                                                    >
                                                        {product.status === 'active' ? (
                                                            <EyeOff size={14} />
                                                        ) : (
                                                            <Eye size={14} />
                                                        )}
                                                    </button>

                                                    <button
                                                        style={{ ...styles.actionBtn, ...styles.actionBtnDanger }}
                                                        onClick={() => deleteProduct(product.id)}
                                                        title="삭제"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* 페이지네이션 */}
                    <div style={styles.paginationContainer}>
                        <div style={styles.paginationInfo}>
                            {startIndex + 1}-{Math.min(endIndex, filteredProducts.length)} / 총 {filteredProducts.length}개
                        </div>

                        {totalPages > 1 && (
                            <div style={styles.pagination}>
                                <button
                                    style={styles.pageButton}
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    <ChevronLeft size={18} />
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                                    if (
                                        page === 1 ||
                                        page === totalPages ||
                                        (page >= currentPage - 2 && page <= currentPage + 2)
                                    ) {
                                        return (
                                            <button
                                                key={page}
                                                style={{
                                                    ...styles.pageButton,
                                                    ...(page === currentPage ? styles.activePageButton : {})
                                                }}
                                                onClick={() => goToPage(page)}
                                            >
                                                {page}
                                            </button>
                                        );
                                    } else if (page === currentPage - 3 || page === currentPage + 3) {
                                        return <span key={page} style={styles.pageEllipsis}>...</span>;
                                    }
                                    return null;
                                })}

                                <button
                                    style={styles.pageButton}
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* 이미지 URL 목록 모달 */}
            {showImageUploadModal && (
                <div style={styles.modalOverlay} onClick={() => setShowImageUploadModal(false)}>
                    <div style={styles.imageModalContent} onClick={(e) => e.stopPropagation()}>
                        <div style={styles.imageModalHeader}>
                            <h2 style={styles.modalTitle}>업로드된 이미지 URL</h2>
                            <button onClick={downloadImageUrls} style={styles.downloadUrlButton}>
                                <Download size={18} />
                                엑셀로 다운로드
                            </button>
                        </div>

                        <p style={styles.imageModalSubtitle}>
                            총 {uploadedImages.length}개의 이미지 | URL을 복사하여 엑셀에 붙여넣으세요
                        </p>

                        <div style={styles.imageList}>
                            {uploadedImages.map((img, index) => (
                                <div key={index} style={styles.imageItem}>
                                    <div style={styles.imagePreviewSmall}>
                                        <img src={img.url} alt={img.fileName} style={styles.imagePreviewImg} />
                                    </div>
                                    <div style={styles.imageInfo}>
                                        <div style={styles.imageFileName}>{img.fileName}</div>
                                        <div style={styles.imageUrl}>{img.url}</div>
                                    </div>
                                    <button onClick={() => copyToClipboard(img.url)} style={styles.copyButton}>
                                        <Copy size={16} />
                                        복사
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div style={styles.imageModalActions}>
                            <button onClick={() => setShowImageUploadModal(false)} style={styles.closeButton}>
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* 엑셀 미리보기 모달 - 계속... */}
            {/* 엑셀 미리보기 모달 */}
            {showExcelModal && (
                <div style={styles.modalOverlay} onClick={() => setShowExcelModal(false)}>
                    <div style={styles.excelModalContent} onClick={(e) => e.stopPropagation()}>
                        <h2 style={styles.excelModalTitle}>엑셀 데이터 미리보기</h2>
                        <p style={styles.modalSubtitle}>
                            총 {excelPreview.length}개 상품 |
                            유효: <span style={{ color: '#10b981' }}>{excelPreview.filter(p => p.isValid).length}</span> |
                            오류: <span style={{ color: '#ef4444' }}>{excelPreview.filter(p => !p.isValid).length}</span>
                        </p>

                        <div style={styles.previewTable}>
                            <table style={styles.table}>
                                <thead>
                                    <tr>
                                        <th style={styles.th}>번호</th>
                                        <th style={styles.th}>상태</th>
                                        <th style={styles.th}>상품명</th>
                                        <th style={styles.th}>카테고리</th>
                                        <th style={styles.th}>판매가</th>
                                        <th style={styles.th}>재고</th>
                                        <th style={styles.th}>오류</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {excelPreview.map((product, idx) => (
                                        <tr key={idx} style={product.isValid ? {} : styles.errorRow}>
                                            <td style={styles.td}>{product.index}</td>
                                            <td style={styles.td}>
                                                {product.isValid ? (
                                                    <CheckCircle size={18} color="#10b981" />
                                                ) : (
                                                    <XCircle size={18} color="#ef4444" />
                                                )}
                                            </td>
                                            <td style={styles.td}>{product.name}</td>
                                            <td style={styles.td}>{getCategoryLabel(product.category)}</td>
                                            <td style={styles.td}>{product.price.toLocaleString()}원</td>
                                            <td style={styles.td}>{product.stock || '-'}</td>
                                            <td style={styles.td}>
                                                {product.errors.length > 0 ? (
                                                    <span style={styles.errorText}>
                                                        {product.errors.join(', ')}
                                                    </span>
                                                ) : (
                                                    '-'
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={styles.excelModalActions}>
                            <button
                                onClick={() => {
                                    setShowExcelModal(false);
                                    setExcelData([]);
                                    setExcelPreview([]);
                                }}
                                style={styles.cancelButton}
                            >
                                취소
                            </button>
                            <button
                                onClick={handleBulkUpload}
                                style={styles.submitButton}
                                disabled={uploadingBulk || excelPreview.filter(p => p.isValid).length === 0}
                            >
                                {uploadingBulk ? '업로드 중...' : `${excelPreview.filter(p => p.isValid).length}개 상품 등록`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

const styles = {
    container: {
        padding: '24px',
        maxWidth: '1600px',
        margin: '0 auto',
        backgroundColor: '#f8fafc',
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flexWrap: 'wrap',
        gap: '16px',
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
    },
    title: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
    },
    subtitle: {
        fontSize: '13px',
        color: '#64748b',
        margin: '4px 0 0 0',
    },
    headerButtons: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap',
    },
    templateButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: 'white',
        color: '#10b981',
        border: '2px solid #10b981',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    imageUploadButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#f59e0b',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    excelUploadButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#10b981',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    addButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    refreshButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        backgroundColor: 'white',
        color: '#64748b',
        border: '2px solid #e2e8f0',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    bulkActionsBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 20px',
        marginBottom: '16px',
        backgroundColor: '#eef2ff',
        borderRadius: '10px',
        border: '2px solid #c7d2fe',
    },
    bulkActionsText: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#6366f1',
    },
    bulkActions: {
        display: 'flex',
        gap: '8px',
    },
    bulkActionButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '6px 12px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    bulkDeleteButton: {
        color: '#ef4444',
    },
    bulkCancelButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '6px 12px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        backgroundColor: 'white',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
    },
    filterSection: {
        marginBottom: '16px',
        padding: '16px',
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    filterRow: {
        display: 'flex',
        gap: '12px',
        flexWrap: 'wrap',
        alignItems: 'center',
    },
    searchBox: {
        flex: 1,
        minWidth: '200px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '0 12px',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        height: '36px',
    },
    searchInput: {
        flex: 1,
        border: 'none',
        outline: 'none',
        fontSize: '13px',
        backgroundColor: 'transparent',
    },
    select: {
        padding: '8px 12px',
        fontSize: '13px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        backgroundColor: 'white',
        cursor: 'pointer',
        outline: 'none',
        height: '36px',
    },
    tableContainer: {
        backgroundColor: 'white',
        borderRadius: '10px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '13px',
    },
    tableHeader: {
        backgroundColor: '#f8fafc',
        borderBottom: '2px solid #e2e8f0',
    },
    th: {
        padding: '12px',
        textAlign: 'left',
        fontWeight: '600',
        color: '#475569',
        whiteSpace: 'nowrap',
    },
    tableRow: {
        borderBottom: '1px solid #f1f5f9',
        transition: 'background-color 0.2s',
        cursor: 'pointer',
    },
    td: {
        padding: '12px',
        verticalAlign: 'middle',
    },
    checkbox: {
        width: '16px',
        height: '16px',
        cursor: 'pointer',
        accentColor: '#6366f1',
    },
    thumbnail: {
        width: '50px',
        height: '50px',
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    thumbnailImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    noThumbnail: {
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
    },
    productNameCell: {
        maxWidth: '300px',
    },
    productNameText: {
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '2px',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    productBrand: {
        fontSize: '11px',
        color: '#94a3b8',
    },
    categoryTag: {
        display: 'inline-block',
        padding: '4px 8px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        borderRadius: '4px',
        whiteSpace: 'nowrap',
    },
    priceCell: {
        display: 'flex',
        flexDirection: 'column',
    },
    price: {
        fontWeight: '700',
        color: '#1e293b',
        whiteSpace: 'nowrap',
    },
    stockAvailable: {
        color: '#059669',
        fontWeight: '600',
    },
    stockOut: {
        color: '#dc2626',
        fontWeight: '600',
    },
    statsCell: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    statItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '11px',
        color: '#64748b',
    },
    dateText: {
        fontSize: '12px',
        color: '#64748b',
    },
    actionButtons: {
        display: 'flex',
        gap: '4px',
    },
    actionBtn: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        backgroundColor: '#f8fafc',
        color: '#6366f1',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    actionBtnSuccess: {
        color: '#10b981',
    },
    actionBtnWarning: {
        color: '#f59e0b',
    },
    actionBtnDanger: {
        color: '#ef4444',
    },
    paginationContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '20px',
        padding: '16px 20px',
        backgroundColor: 'white',
        borderRadius: '10px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    paginationInfo: {
        fontSize: '13px',
        color: '#64748b',
        fontWeight: '500',
    },
    pagination: {
        display: 'flex',
        gap: '4px',
    },
    pageButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '32px',
        height: '32px',
        padding: '0 8px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    activePageButton: {
        color: 'white',
        backgroundColor: '#6366f1',
        borderColor: '#6366f1',
    },
    pageEllipsis: {
        padding: '0 8px',
        color: '#94a3b8',
        display: 'flex',
        alignItems: 'center',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '80px 24px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    emptyText: {
        fontSize: '15px',
        color: '#64748b',
        margin: '16px 0',
    },
    emptyButton: {
        padding: '10px 20px',
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: '#6366f1',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        marginTop: '8px',
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '400px',
        gap: '16px',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid #e2e8f0',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    loadingText: {
        fontSize: '14px',
        color: '#64748b',
    },
    // 모달 스타일
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
    },
    imageModalContent: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    imageModalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '24px',
        borderBottom: '1px solid #e2e8f0',
    },
    modalTitle: {
        margin: 0,
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a',
    },
    downloadUrlButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 16px',
        backgroundColor: '#10b981',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    imageModalSubtitle: {
        margin: '0',
        padding: '12px 24px',
        fontSize: '13px',
        color: '#64748b',
        backgroundColor: '#f8fafc',
    },
    imageList: {
        flex: 1,
        overflow: 'auto',
        padding: '16px 24px',
    },
    imageItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
        marginBottom: '8px',
    },
    imagePreviewSmall: {
        width: '60px',
        height: '60px',
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: '#e2e8f0',
        flexShrink: 0,
    },
    imagePreviewImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    imageInfo: {
        flex: 1,
        minWidth: 0,
    },
    imageFileName: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '4px',
    },
    imageUrl: {
        fontSize: '11px',
        color: '#64748b',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    copyButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '6px 12px',
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: '600',
        cursor: 'pointer',
        flexShrink: 0,
    },
    imageModalActions: {
        padding: '16px 24px',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'center',
    },
    closeButton: {
        padding: '8px 24px',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    excelModalContent: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        maxWidth: '1200px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    excelModalTitle: {
        margin: '0 0 8px 0',
        fontSize: '20px',
        fontWeight: '700',
        color: '#0f172a',
        padding: '24px 24px 0',
    },
    modalSubtitle: {
        margin: '0 0 16px 0',
        fontSize: '13px',
        color: '#64748b',
        padding: '0 24px',
    },
    previewTable: {
        flex: 1,
        overflow: 'auto',
        padding: '0 24px',
    },
    errorRow: {
        backgroundColor: '#fef2f2',
    },
    errorText: {
        color: '#dc2626',
        fontSize: '11px',
    },
    excelModalActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '8px',
        padding: '16px 24px',
        borderTop: '1px solid #e2e8f0',
    },
    cancelButton: {
        padding: '8px 20px',
        backgroundColor: '#f1f5f9',
        color: '#64748b',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
    submitButton: {
        padding: '8px 20px',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: '600',
        cursor: 'pointer',
    },
};

export default ProductManagement;